import type { AxiosRequestConfig } from "axios";

import {
  AssetItem,
  AssetListResponse,
  Breakdown,
  ComplianceScore,
  ComplianceTrend,
  CreateAssetRequest,
  HistoricalTrends,
  HumidityCard,
  IncidentStats,
  MetricCard,
  ThermalCard,
  TrendMode,
  UpdateAssetRequest,
} from "entities/assets";
import { DELETE, GET, PATCH, POST } from "utils/http";

const baseUrl = "/api/assets";

// A missing route or single resource shouldn't trigger the global NOT_FOUND redirect.
const noRedirect = { skipAuthRedirect: true };

export const getList = (query = "") => GET<AssetListResponse>(`${baseUrl}${query}`, noRedirect);

export const getCurrent = (id: number | string) => GET<AssetItem>(`${baseUrl}/${id}`);

export const getComplianceScore = (id: number | string) =>
  GET<ComplianceScore>(`${baseUrl}/${id}/compliance-score`);

export const getIncidentStats = (id: number | string) =>
  GET<IncidentStats>(`${baseUrl}/${id}/incident-stats`);

export const getMetricCard = (id: number | string, metric: string) =>
  GET<MetricCard>(`${baseUrl}/${id}/metrics/${metric}`);

export const getThermalCard = (id: number | string, metric: string) =>
  GET<ThermalCard>(`${baseUrl}/${id}/thermal/${metric}`);

export const getHumidityCard = (id: number | string) =>
  GET<HumidityCard>(`${baseUrl}/${id}/humidity`);

export const getComplianceTrend = (id: number | string, mode: TrendMode, date?: string) => {
  const params = new URLSearchParams({ mode });
  if (date) params.set("date", date);
  return GET<ComplianceTrend>(`${baseUrl}/${id}/compliance-trend?${params.toString()}`);
};

export const getIncidentTrend = (id: number | string, mode: TrendMode, date?: string) => {
  const params = new URLSearchParams({ mode });
  if (date) params.set("date", date);
  return GET<ComplianceTrend>(`${baseUrl}/${id}/incident-trend?${params.toString()}`);
};

export const getBreakdown = (id: number | string, metric: string) =>
  GET<Breakdown>(`${baseUrl}/${id}/breakdown/${metric}`);

export const getHistoricalTrends = (id: number | string, period: string) =>
  GET<HistoricalTrends>(`${baseUrl}/${id}/historical-trends?period=${period}`);

export const create = (body: CreateAssetRequest) => POST<CreateAssetRequest, AssetItem>(`${baseUrl}`, body);

export const update = (id: number | string, body: UpdateAssetRequest, config?: AxiosRequestConfig) =>
  PATCH<UpdateAssetRequest, AssetItem>(`${baseUrl}/${id}`, body, config);

export const remove = (id: number | string) => DELETE(`${baseUrl}/${id}`);
